import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Film, Tv, Trophy, Swords, ExternalLink, CheckCircle2, Calendar, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const SITE_URL = "https://roidustreaming.fr";

type PageType = "football" | "ufc" | "movie" | "series";

interface PublishedPage {
  slug: string;
  title: string;
  created_at: string;
  type: PageType;
  route: string;
}

const PAGE_TYPE_CONFIG: Record<PageType, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  route: string;
}> = {
  football: {
    label: "Football",
    icon: <Trophy className="w-4 h-4" />,
    color: "text-green-400",
    bgColor: "bg-green-400/10",
    borderColor: "border-green-400/30",
    route: "football",
  },
  ufc: {
    label: "UFC",
    icon: <Swords className="w-4 h-4" />,
    color: "text-red-400",
    bgColor: "bg-red-400/10",
    borderColor: "border-red-400/30",
    route: "ufc",
  },
  movie: {
    label: "Films",
    icon: <Film className="w-4 h-4" />,
    color: "text-primary",
    bgColor: "bg-primary/10",
    borderColor: "border-primary/30",
    route: "films",
  },
  series: {
    label: "Séries",
    icon: <Tv className="w-4 h-4" />,
    color: "text-blue-400",
    bgColor: "bg-blue-400/10",
    borderColor: "border-blue-400/30",
    route: "series",
  },
};

// Compact page list with expand/collapse
function PageList({ pages, type }: { pages: PublishedPage[]; type: PageType }) {
  const [expanded, setExpanded] = useState(false);
  const config = PAGE_TYPE_CONFIG[type];
  const PREVIEW_COUNT = 5;
  const shown = expanded ? pages : pages.slice(0, PREVIEW_COUNT);

  if (pages.length === 0) {
    return (
      <p className="text-muted-foreground text-sm italic py-2">Aucune page créée aujourd'hui</p>
    );
  }

  return (
    <div className="space-y-1">
      {shown.map((page) => (
        <div key={page.slug} className="flex items-center justify-between gap-2 py-1 border-b border-border/50 last:border-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
            <span className="text-sm text-foreground truncate">{page.title}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="outline" className="text-xs font-mono border-green-400/30 text-green-400 bg-green-400/5">
              ✓ publié
            </Badge>
            <a
              href={`${SITE_URL}/${config.route}/${page.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-1 text-xs hover:underline ${config.color} shrink-0`}
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      ))}
      {pages.length > PREVIEW_COUNT && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground hover:text-foreground mt-1 h-7"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <><ChevronUp className="w-3 h-3 mr-1" /> Voir moins</>
          ) : (
            <><ChevronDown className="w-3 h-3 mr-1" /> Voir les {pages.length - PREVIEW_COUNT} autres</>
          )}
        </Button>
      )}
    </div>
  );
}

// Card for a single content type
function TypePublicationCard({ type, date }: { type: PageType; date: string }) {
  const config = PAGE_TYPE_CONFIG[type];

  const { data: pages = [], isLoading, refetch } = useQuery({
    queryKey: ["seo-publications", type, date],
    queryFn: async () => {
      let result: PublishedPage[] = [];

      if (type === "football") {
        const { data } = await supabase
          .from("football_matches")
          .select("slug, home_team, away_team, created_at")
          .eq("published", true)
          .gte("created_at", `${date}T00:00:00`)
          .lte("created_at", `${date}T23:59:59`)
          .order("created_at", { ascending: false });
        result = (data || []).map((m) => ({
          slug: m.slug,
          title: `${m.home_team} - ${m.away_team}`,
          created_at: m.created_at,
          type: "football",
          route: "football",
        }));
      } else if (type === "ufc") {
        const { data } = await supabase
          .from("ufc_fights")
          .select("slug, fighter_a, fighter_b, created_at")
          .eq("published", true)
          .gte("created_at", `${date}T00:00:00`)
          .lte("created_at", `${date}T23:59:59`)
          .order("created_at", { ascending: false });
        result = (data || []).map((f) => ({
          slug: f.slug,
          title: `${f.fighter_a} vs ${f.fighter_b}`,
          created_at: f.created_at,
          type: "ufc",
          route: "ufc",
        }));
      } else if (type === "movie") {
        const { data } = await supabase
          .from("movies")
          .select("slug, title, created_at")
          .eq("published", true)
          .gte("created_at", `${date}T00:00:00`)
          .lte("created_at", `${date}T23:59:59`)
          .order("created_at", { ascending: false });
        result = (data || []).map((m) => ({
          slug: m.slug,
          title: m.title,
          created_at: m.created_at,
          type: "movie",
          route: "films",
        }));
      } else if (type === "series") {
        const { data } = await supabase
          .from("series")
          .select("slug, title, created_at")
          .eq("published", true)
          .gte("created_at", `${date}T00:00:00`)
          .lte("created_at", `${date}T23:59:59`)
          .order("created_at", { ascending: false });
        result = (data || []).map((s) => ({
          slug: s.slug,
          title: s.title,
          created_at: s.created_at,
          type: "series",
          route: "series",
        }));
      }
      return result;
    },
    refetchInterval: 120000,
  });

  return (
    <Card className={`bg-card border ${config.borderColor}`}>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={config.color}>{config.icon}</span>
            <CardTitle className={`text-sm font-semibold ${config.color}`}>{config.label}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {isLoading ? (
              <RefreshCw className="w-3 h-3 text-muted-foreground animate-spin" />
            ) : (
              <Badge className={`${config.bgColor} ${config.color} border-0 text-xs font-bold`}>
                {pages.length} page{pages.length !== 1 ? "s" : ""}
              </Badge>
            )}
            <Badge
              variant="outline"
              className="text-xs border-green-400/40 text-green-400 bg-green-400/5"
            >
              <CheckCircle2 className="w-2.5 h-2.5 mr-1" />
              Indexation envoyée
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-6 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <PageList pages={pages} type={type} />
        )}
      </CardContent>
    </Card>
  );
}

const AdminSeoPublicationsSection = () => {
  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(today);

  // Build last 7 days options
  const dateOptions = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const val = d.toISOString().split("T")[0];
    const label = i === 0 ? "Aujourd'hui" : i === 1 ? "Hier" : d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" });
    return { value: val, label };
  });

  // Summary counts per type for selected date
  const { data: summaryCounts } = useQuery({
    queryKey: ["seo-publications-summary", selectedDate],
    queryFn: async () => {
      const [football, ufc, movies, series] = await Promise.all([
        supabase.from("football_matches").select("id", { count: "exact", head: true }).eq("published", true).gte("created_at", `${selectedDate}T00:00:00`).lte("created_at", `${selectedDate}T23:59:59`),
        supabase.from("ufc_fights").select("id", { count: "exact", head: true }).eq("published", true).gte("created_at", `${selectedDate}T00:00:00`).lte("created_at", `${selectedDate}T23:59:59`),
        supabase.from("movies").select("id", { count: "exact", head: true }).eq("published", true).gte("created_at", `${selectedDate}T00:00:00`).lte("created_at", `${selectedDate}T23:59:59`),
        supabase.from("series").select("id", { count: "exact", head: true }).eq("published", true).gte("created_at", `${selectedDate}T00:00:00`).lte("created_at", `${selectedDate}T23:59:59`),
      ]);
      return {
        football: football.count || 0,
        ufc: ufc.count || 0,
        movies: movies.count || 0,
        series: series.count || 0,
        total: (football.count || 0) + (ufc.count || 0) + (movies.count || 0) + (series.count || 0),
      };
    },
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Pages créées & indexées
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pages publiées avec demande d'indexation Google automatique
          </p>
        </div>
        <Select value={selectedDate} onValueChange={setSelectedDate}>
          <SelectTrigger className="w-[160px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Card className="bg-card border-border col-span-2 sm:col-span-1">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-foreground">{summaryCounts?.total ?? "—"}</p>
            <p className="text-xs text-muted-foreground">Total publié</p>
          </CardContent>
        </Card>
        {(["football", "ufc", "movie", "series"] as PageType[]).map((type) => {
          const config = PAGE_TYPE_CONFIG[type];
          const count = type === "movie" ? summaryCounts?.movies : summaryCounts?.[type as "football" | "ufc" | "series"];
          return (
            <Card key={type} className="bg-card border-border">
              <CardContent className="p-3 text-center">
                <span className={`flex justify-center mb-1 ${config.color}`}>{config.icon}</span>
                <p className={`text-2xl font-bold ${config.color}`}>{count ?? "—"}</p>
                <p className="text-xs text-muted-foreground">{config.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Indexation status notice */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-green-400/5 border border-green-400/20">
        <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
        <p className="text-xs text-green-300">
          <strong>Indexation automatique active :</strong> Chaque page publiée déclenche automatiquement une notification à Google Indexing API et IndexNow (Bing/Yandex). Le sitemap est également pingué.
        </p>
      </div>

      {/* 4 type cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(["football", "ufc", "movie", "series"] as PageType[]).map((type) => (
          <TypePublicationCard key={`${type}-${selectedDate}`} type={type} date={selectedDate} />
        ))}
      </div>
    </div>
  );
};

export default AdminSeoPublicationsSection;
